<template>
    <div class="user-chat overflow-hidden" :class="{ 'user-chat-show': activeContact }"
        style="flex-grow: 1; min-width: 0;" @dragover.prevent="isDragging = true" @dragenter.prevent="isDragging = true"
        @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop">
        <transition name="fade">
            <div v-if="isDragging && activeContact" class="drag-overlay">
                <div class="drag-content">
                    <i class="ri-upload-cloud-2-line drag-icon"></i>
                    <h3>Solte os arquivos aqui</h3>
                </div>
            </div>
        </transition>

        <div v-if="activeContact" class="d-flex flex-column h-100 chat-area-wrapper position-relative">

            <ChatHeader :contact="activeContact" :search-result-count="searchResultCount"
                @select-messages="handleSelectMessages" @mute-chat="handleMuteChat" @favorite-chat="handleFavoriteChat"
                @open-link-tag-modal="handleOpenLinkTagModal" @open-history="handleOpenHistory"
                @transfer-chat="handleTransferChat" @forward-mass="handleForwardMass" @trigger-bot="handleTriggerBot"
                @stop-bot="handleStopBot" @end-session="handleFinishAtendimento" @search="handleSearch"
                @search-next="handleSearchNext" @search-prev="handleSearchPrev" @search-close="handleSearchClose" />

            <ChatConversation ref="conversation" :messages="messages"
                :contactAvatar="activeContact?.avatar ?? undefined" :message-to-highlight="messageIdToScrollTo"
                :highlight-query="currentSearchQuery" :is-sidebar-open="isProfileOpen" exibir-anotacoes exibir-nao-lidas @reply-message="handleReply"
                @edit-message="handleEdit" @delete-message="handleDelete" @react-message="handleReact"
                @forward-message="handleForward"  @star-message="handleStar"
                @pin-message="handlePin" />

            <ChatInput />

            <FilePreviewModal />
            <ListMessageModal />
        </div>

        <ChatEmpty v-else />
        <MediaViewerModal />

        <div class="global-indicator-stack">
            <!-- Conversão de mídia (vídeo ou imagem): um cartão por arquivo, com progresso real e
                 cancelamento. Fica fora do fluxo (não bloqueia a tela) porque
                 converter leva minutos e o atendente segue atendendo. -->
            <transition-group name="slide-up-fade">
                <div v-for="conversao in ongoingConversions" :key="conversao.id"
                    class="global-upload-indicator conversion-indicator"
                    :title="conversao.fileName">
                    <div class="upload-indicator-content">
                        <div class="upload-spinner" :class="{ 'is-indeterminate': conversao.progress === 0 }">
                            <svg class="circular-chart" viewBox="0 0 36 36">
                                <path class="circle-bg"
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                                <path class="circle circle-conversion"
                                    :stroke-dasharray="conversao.progress === 0 ? '25, 100' : `${conversao.progress}, 100`"
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                            </svg>
                            <i class="upload-icon icon-conversion"
                                :class="ehEtapaDeImagem(conversao.etapa) ? 'ri-image-line' : 'ri-film-line'"></i>
                        </div>
                        <div class="upload-details">
                            <span class="upload-title">{{ rotuloDaEtapa(conversao.etapa) }}</span>
                            <span class="upload-subtitle">{{ conversao.fileName }}</span>
                        </div>
                        <span v-if="conversao.progress > 0" class="conversion-pct">{{ conversao.progress }}%</span>
                        <button type="button" class="conversion-cancel"
                            title="Cancelar conversão" aria-label="Cancelar conversão"
                            @click.stop="conversao.cancelar()">
                            <i class="ri-close-line"></i>
                        </button>
                    </div>
                </div>
            </transition-group>

            <transition name="slide-up-fade">
                <div v-if="ongoingUploads.length > 0" class="global-upload-indicator" @click="goToUploadChat" title="Clique para abrir a conversa">
                    <div class="upload-indicator-content">
                        <div class="upload-spinner">
                            <svg class="circular-chart" viewBox="0 0 36 36">
                                <path class="circle-bg"
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                                <path class="circle"
                                    :stroke-dasharray="`${overallProgress}, 100`"
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                            </svg>
                            <i class="ri-upload-cloud-2-line upload-icon"></i>
                        </div>
                        <div class="upload-details">
                            <span class="upload-title">Enviando {{ ongoingUploads.length }} mídia(s)</span>
                            <span class="upload-subtitle">{{ ongoingUploads[0].fileName }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { ChatStore, useChatStore } from '@/stores/app/chatStore';
import { useInternalChatStore } from '@/stores/app/chatInternoStore';
import { useUiStore } from '@/stores/app/uiStore';
import { toast } from 'vue3-toastify';
import Swal from 'sweetalert2';
import { resolveSelectableMessageIds } from '@/utils/resolveSelectableMessageIds';

import ChatHeader from './ChatHeader.vue';
import ChatConversation from './ChatConversation.vue';
import ChatInput from './ChatInput.vue';
import ChatEmpty from './ChatEmpty.vue';
import FilePreviewModal from '@/components/app/modals/FilePreviewModal.vue';
import MediaViewerModal from '@/components/app/modals/MediaViewerModal.vue';
import ListMessageModal from '@/components/app/modals/ListMessageModal.vue';
import { PromptToFinalizarAtendimento } from '../../swal/FinalizarAtendimento';
import { Chat, Message } from '@/mocks/app/mockChatData';
import { IdAtendimento } from '@/services/common-types';
import { globalUploadsState, globalVideoConversionsState } from '@/stores/app/chatStore';
import type { EtapaDeConversao } from '@/utils/video/converterVideoParaMeta';
import type { EtapaDeImagem } from '@/utils/imagem/converterImagemParaMeta';
import { onMounted, onBeforeUnmount } from 'vue';

// Previne o F5/fechamento da aba se houver uploads em andamento
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (ongoingUploads.value.length > 0) {
        event.preventDefault();
        event.returnValue = 'Você tem arquivos sendo enviados. Tem certeza que deseja sair? O envio será cancelado.';
        return event.returnValue;
    }
};

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
});

const chatStoreNormal = useChatStore();
const internalChatStore = useInternalChatStore();
const uiStore = useUiStore();
const conversation = ref<InstanceType<typeof ChatConversation> | null>(null);


// Dynamically determine which store to use based on the chat mode
const chatStore = computed(() => {
    if (internalChatStore.isInternalChatMode) {
        return internalChatStore;
    }

    return chatStoreNormal;
});

const isProfileOpen = computed(() => chatStoreNormal.isProfileOpen);
const activeContact = computed(() => chatStore.value.activeChat);
const isLoadingMessages = computed(() => chatStore.value.isLoadingMessages);
// const messages = computed(() => chatStore.value.messages);
const messages = ref([] as Message[]);
watch(() => chatStore?.value?.messages, (allMessages) => messages.value = allMessages || [], {immediate: true, deep: 2});

const messageIdToScrollTo = computed(() => uiStore.messageIdToScrollTo);

const searchResults = ref<string[]>([]); // Armazena os IDs das mensagens
const activeSearchIndex = ref(-1);
const currentSearchQuery = ref('');

const searchResultCount = computed(() => ({
    current: activeSearchIndex.value + 1,
    total: searchResults.value.length,
}));

const isDragging = ref(false);

const ongoingUploads = computed(() => globalUploadsState.ongoingUploads || []);
const ongoingConversions = computed(() => globalVideoConversionsState.ongoing || []);

const ROTULOS_DE_ETAPA: Record<EtapaDeConversao | EtapaDeImagem, string> = {
    convertendo: 'Convertendo vídeo',
    // Nomeia a segunda passada em vez de deixar o anel voltar a zero sem
    // explicação: acontece quando o encoder do sistema estoura o limite do
    // canal e o ffmpeg precisa refazer.
    recomprimindo: 'Recomprimindo vídeo',
    'convertendo-imagem': 'Convertendo imagem',
    'comprimindo-imagem': 'Comprimindo imagem',
};
const ehEtapaDeImagem = (etapa: EtapaDeConversao | EtapaDeImagem) => etapa.endsWith('-imagem');
const rotuloDaEtapa = (etapa: EtapaDeConversao | EtapaDeImagem) =>
    ROTULOS_DE_ETAPA[etapa] ?? (ehEtapaDeImagem(etapa) ? 'Preparando imagem' : 'Preparando vídeo');
const overallProgress = computed(() => {
    if (ongoingUploads.value.length === 0) return 0;
    const total = ongoingUploads.value.reduce((acc: number, u: any) => acc + u.progress, 0);
    return total / ongoingUploads.value.length;
});

function goToUploadChat() {
    if (ongoingUploads.value.length > 0) {
        const targetChatId = ongoingUploads.value[0].chatId;
        // Substitua as variáveis de store abaixo para as que você já usa no seu componente
        if (targetChatId !== chatStoreNormal.activeChatId) {
            chatStoreNormal.selectChat(targetChatId as any); 
        }
    }
}

function handleDragLeave(e: DragEvent) {
    // Lógica simples para evitar que o overlay pisque ao passar por filhos
    if (e.clientX === 0 && e.clientY === 0) {
        isDragging.value = false;
    }
}

// Quando soltar o arquivo
function handleDrop(e: DragEvent) {
    isDragging.value = false;
    const files = e.dataTransfer?.files;

    if (files && files.length > 0) {
        const filesArray = Array.from(files);
        uiStore.openFilePreviewModal(filesArray);

        // if (files.length > 1) {
        //     for (let i = 1; i < files.length; i++) {
        //         uiStore.addFileToPreview(files[i]);
        //     }
        // }
    }
}

function scrollToActiveMatch() {
    if (activeSearchIndex.value === -1 || searchResults.value.length === 0) {
        uiStore.clearScrollTo(); // Limpa qualquer destaque
        return;
    }
    const messageId = searchResults.value[activeSearchIndex.value];
    uiStore.scrollToMessage(messageId);
}

function handleSearch(query: string) {
    const normalizedQuery = query.toLowerCase();

    // Se a query for a mesma, apenas vai para o próximo (ou primeiro)
    if (normalizedQuery === currentSearchQuery.value) {
        handleSearchNext();
        return;
    }

    currentSearchQuery.value = normalizedQuery;

    // Filtra as mensagens que contêm o texto
    const matches = messages.value
        .filter(m => m.text && m.text.toLowerCase().includes(normalizedQuery))
        .map(m => m.id); // Pega apenas os IDs

    searchResults.value = matches.reverse(); // Inverte para começar do mais recente

    if (matches.length > 0) {
        activeSearchIndex.value = 0;
        scrollToActiveMatch();
    } else {
        activeSearchIndex.value = -1;
        uiStore.clearScrollTo();
        toast.warning("Nenhum resultado encontrado.");
    }
}

// Navega para o próximo resultado
function handleSearchNext() {
    if (searchResults.value.length === 0) return;

    // % (módulo) faz o "wrap around" (volta ao início)
    activeSearchIndex.value = (activeSearchIndex.value + 1) % searchResults.value.length;
    scrollToActiveMatch();
}

// Navega para o resultado anterior
function handleSearchPrev() {
    if (searchResults.value.length === 0) return;

    // Lógica para o "wrap around" (indo para trás)
    activeSearchIndex.value = (activeSearchIndex.value - 1 + searchResults.value.length) % searchResults.value.length;
    scrollToActiveMatch();
}

// Limpa o estado da pesquisa
function handleSearchClose() {
    searchResults.value = [];
    activeSearchIndex.value = -1;
    currentSearchQuery.value = '';
    uiStore.clearScrollTo(); // Remove o destaque
}

function scrollToBottom() {
    nextTick(() => {
        if (conversation.value) {
            conversation.value.scrollToBottom();
        }
    });
}

const lastMessageId = ref<string | undefined>(undefined);

watch(activeContact, (newContact) => {
    if (newContact) {
        lastMessageId.value = undefined;

        const unwatch = watch(isLoadingMessages, (loading) => {
            if (!loading) {
                if (messages.value.length > 0) {
                    lastMessageId.value = messages.value[messages.value.length - 1].id;
                }

                // A abertura agora é do ChatConversation: com não lidas, no separador;
                // sem elas, a mesma `scrollToBottomAnimated` que era chamada aqui.
                // Chamada daqui, rolava para o fim também quando havia não lidas.

                unwatch();
            }
        });
    }
});

// MODIFICADO: Removemos o scroll forçado daqui.
// O ChatConversation agora cuida disso. Apenas atualizamos o lastMessageId.
watch(() => messages.value.length, () => {
    nextTick(() => {
        if (!conversation.value) return;

        const currentLastMsg = messages.value[messages.value.length - 1];
        const currentLastId = currentLastMsg?.id;

        if (currentLastId !== lastMessageId.value) {
            lastMessageId.value = currentLastId;
            // REMOVIDO: conversation.value.scrollToBottomSmooth(); 
        }
    });
});

function handleReply(message: Message) {
    if (internalChatStore.activeChatId) {
        internalChatStore.setReplyMessage(message);
        return;
    }

    chatStore.value.setReplyMessage(message);
}

function handleEdit(message: Message) {
    chatStore.value.setEditMessage(message);
}
async function handleDelete(message: Message) {
    // Um balão de álbum agrupa várias mídias: o aviso precisa dizer quantas somem.
    const qtd = resolveSelectableMessageIds(message).length;

    const result = await Swal.fire({
        title: 'Você tem certeza?',
        text: qtd > 1
            ? `Isto apaga as ${qtd} mídias deste grupo. Você não poderá reverter esta ação!`
            : "Você não poderá reverter esta ação!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, apagar!',
        cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;
    try {
        // `deleteMessage` trata os próprios erros: sem checar o retorno, o sucesso era
        // anunciado mesmo quando nada era apagado.
        const apagou = await chatStore.value.deleteMessage(message);
        if (apagou) toast.success(qtd > 1 ? "Mensagens apagadas." : "Mensagem apagada.");
    } catch (e) {
        console.error(e);
        toast.error((e as Error).message || 'Não foi possível excluir a mensagem.')
    }
}
function handleReact(message: Message, emoji: string) {
    chatStore.value.toggleReaction(message, emoji);
}
function handleForward(message: Message) {
    chatStore.value.startForwarding(message);}
function handleStar(message: Message) {
    chatStore.value.toggleFavoriteMessage(message.id);
}
function handlePin(message: Message) {
    const existingPin = chatStore.value.mensagensFixadas.find(p => p.idMensagem === message.id);
    if (existingPin) {
        chatStore.value.unpinMessage(existingPin.id);
    } else {
        uiStore.openPinModal(message);
    }
}

// ==============================================
// 🚀 NOVOS HANDLERS (DO CHAT HEADER)
// ==============================================
function handleSelectMessages() {
    console.log("AÇÃO: Selecionar Mensagens (V2)");
    chatStore.value.startSelectionMode();
}

function handleMuteChat() {
    console.log("AÇÃO: Silenciar/Remover Silêncio");
    if (activeContact.value && chatStore.value.activeChat) {
        chatStore.value.toggleMute(activeContact.value.id);
        const toastMsg = chatStore.value.activeChat.isMuted ? 'Notificações silenciadas' : 'Notificações ativadas';
        toast.success(toastMsg);
    }
}

function handleFavoriteChat() {
    console.log("AÇÃO: Favoritar/Desfavoritar Chat");
    if (activeContact.value) {
        chatStore.value.toggleFavorite(activeContact.value.id);
    }
}

function handleOpenLinkTagModal() {
    console.log("AÇÃO: Abrir Modal de Etiquetas");
    uiStore.openLinkTagModal();
}

function handleOpenHistory() {
    console.log("AÇÃO: Abrir Histórico (V1)");
    // Lógica para abrir o modal de histórico (V1)
}

function handleTransferChat() {
    console.log("AÇÃO: Abrir Modal de Transferir (V1)");
    // Lógica para abrir o modal de transferência (V1)
}

function handleForwardMass() {
    chatStore.value.startMassForwarding();
}

function handleTriggerBot() {
    uiStore.openBotTriggerModal();
}

async function handleStopBot() {
    if (!chatStore.value.activeChatId) return;
    await chatStoreNormal.interromperBot(chatStore.value.activeChatId as IdAtendimento);
    toast.success('Bot interrompido');
}

function handleFinishAtendimento() {
    if (!chatStore.value.activeChat) throw new Error('[]')
    if (internalChatStore.isInternalChatMode) return; // de facto grupos não podem ser finalizados
    PromptToFinalizarAtendimento(chatStore.value.activeChat as Chat, chatStore.value as ChatStore)
}
</script>

<style scoped>
.user-chat {
    height: 100vh;
    height: 100dvh;
    background-color: var(--bs-white);
    transition: all 0.4s;
    /* Suaviza a transição no mobile */
}

[data-bs-theme="light"] .user-chat {
    background-color: var(--brand-user-chat-bg);
}

.chat-area-wrapper {
    height: 100vh;
    height: 100dvh;
}

.loading-messages {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    color: var(--bs-text-muted);
}

.spin {
    display: inline-block;
    animation: spin 1s linear infinite;
    margin-right: 8px;
    font-size: 1.3rem;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.user-chat {
    height: 100vh;
    height: 100dvh;
    background-color: var(--bs-white);
}

.chat-area-wrapper {
    height: 100vh;
    height: 100dvh;
    position: relative !important;
    overflow: hidden;
}

.drag-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 2000;
    /* Acima de tudo no chat */
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    /* Permite soltar "através" dele */
}

[data-bs-theme="dark"] .drag-overlay {
    background-color: rgba(34, 46, 53, 0.95);
}

.drag-content {
    text-align: center;
    color: #54656f;
}

[data-bs-theme="dark"] .drag-content {
    color: #e9edef;
}

.drag-icon {
    font-size: 80px;
    margin-bottom: 20px;
    color: var(--bs-success);
}

.drag-content h3 {
    font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* === RESPONSIVIDADE MOBILE === */
@media (max-width: 991.98px) {
    .user-chat {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 100vh;
        height: 100dvh;
        z-index: 99;
        transform: translateX(100%);
        visibility: hidden;
    }

    .user-chat.user-chat-show {
        transform: translateX(0);
        visibility: visible;
    }
}

/* === BALÕES GLOBAIS (conversão de vídeo + upload) === */

/* A pilha é quem se posiciona; os cartões só se empilham dentro dela. Sem isto
   um cartão de conversão e um de upload simultâneos ficariam um em cima do
   outro, já que ambos eram `position: absolute` no mesmo canto. */
.global-indicator-stack {
    position: absolute;
    bottom: 24px;
    right: 24px;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    /* A pilha cobre o canto inteiro mesmo vazia; só os cartões recebem clique. */
    pointer-events: none;
}

.global-upload-indicator {
    pointer-events: auto;
    background: var(--bs-white);
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    border: 1px solid var(--bs-border-color);
    transition: transform 0.2s, box-shadow 0.2s;
}

[data-bs-theme="dark"] .global-upload-indicator {
    background: #1e293b;
    border-color: rgba(255,255,255,0.05);
}

.global-upload-indicator:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.upload-indicator-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.upload-spinner {
    position: relative;
    width: 38px;
    height: 38px;
}

.circular-chart {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    max-height: 250px;
}

.circle-bg {
    fill: none;
    stroke: var(--bs-light-bg-subtle, #f0f2f5);
    stroke-width: 3.8;
}

[data-bs-theme="dark"] .circle-bg { stroke: #334155; }

.circle {
    fill: none;
    stroke-width: 2.8;
    stroke-linecap: round;
    transition: stroke-dasharray 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    stroke: var(--bs-success);
}

.upload-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 16px;
    color: var(--bs-success);
}

.upload-details {
    display: flex;
    flex-direction: column;
}

.upload-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--bs-heading-color);
}

.upload-subtitle {
    font-size: 12px;
    color: var(--bs-text-muted);
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* --- Cartão de conversão de vídeo --- */
.conversion-indicator {
    /* Não leva a lugar nenhum: o clique útil aqui é o de cancelar. */
    cursor: default;
}

.conversion-indicator:hover {
    transform: none;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.circle-conversion { stroke: var(--bs-primary); }
.icon-conversion { color: var(--bs-primary); }

/* Progresso 0 = ainda não há medida real (o core do ffmpeg está baixando, ou o
   encoder ainda não entregou o primeiro quadro). Anel parado em 0% passa
   impressão de travado; girando um arco curto, diz "trabalhando". */
.upload-spinner.is-indeterminate .circular-chart {
    animation: conversion-spin 1s linear infinite;
    transform-origin: 50% 50%;
}

.upload-spinner.is-indeterminate .circle-conversion {
    transition: none;
}

@keyframes conversion-spin {
    to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .upload-spinner.is-indeterminate .circular-chart { animation: none; }
}

.conversion-pct {
    font-size: 12px;
    font-weight: 600;
    color: var(--bs-primary);
    font-variant-numeric: tabular-nums;
    margin-left: 4px;
}

.conversion-cancel {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: 2px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--bs-text-muted);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;
}

.conversion-cancel:hover {
    background: var(--bs-danger-bg-subtle, #f8d7da);
    color: var(--bs-danger);
}

.slide-up-fade-enter-active, .slide-up-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-up-fade-enter-from, .slide-up-fade-leave-to {
    transform: translateY(20px) scale(0.95);
    opacity: 0;
}
</style>